import { useEffect, useState } from "react";
import { useSearch, useNavigate } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";

interface Customer {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  emailVerified: boolean;
  createdAt: string;
}

interface Watch {
  id: string;
  name: string;
  brand: string | null;
  model: string | null;
  reference: string | null;
  liftAngleDeg: number;
  targetBph: number | null;
  hasPhoto: boolean;
  photoDataUrl?: string;
  createdAt: string;
}

interface Measurement {
  id: string;
  timestamp: string;
  rate: number;
  rateRegression: number | null;
  beatError: number;
  amplitude: number | null;
  bph: number;
  kalmanFilteredRate: number | null;
}

function fmtDate(iso: string) {
  return iso.slice(0, 10).replace(/-/g, ".");
}

function bestRate(m: Measurement) {
  return m.kalmanFilteredRate ?? m.rateRegression ?? m.rate;
}

function fullName(c: Customer) {
  return [c.firstName, c.lastName].filter(Boolean).join(" ");
}

const infoRow: React.CSSProperties = {
  padding: "16px 22px",
  borderBottom: "1px solid var(--line)",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: 12,
};

const infoKey: React.CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 10,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--ink-mute)",
};

const infoVal: React.CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 13,
  color: "var(--ink)",
  letterSpacing: "0.04em",
  wordBreak: "break-word",
  textAlign: "right",
};

export function CustomerPage() {
  const { id } = useSearch({ strict: false }) as { id: string };
  const navigate = useNavigate();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [watches, setWatches] = useState<Watch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [expanded, setExpanded] = useState<string | null>(null);
  const [measurements, setMeasurements] = useState<Record<string, Measurement[]>>(
    {},
  );

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all([
      api.get<Customer>(`/admin/users/${id}`),
      api.get<Watch[]>(`/admin/users/${id}/watches`),
    ])
      .then(([c, w]) => {
        if (cancelled) return;
        setCustomer(c.data);
        setWatches(w.data);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Failed to load customer");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  function toggleWatch(watchId: string) {
    const next = expanded === watchId ? null : watchId;
    setExpanded(next);
    if (next && !measurements[next]) {
      api
        .get<Measurement[]>(`/admin/users/${id}/watches/${watchId}/measurements`)
        .then(({ data }) =>
          setMeasurements((prev) => ({ ...prev, [watchId]: data })),
        )
        .catch(() =>
          setMeasurements((prev) => ({ ...prev, [watchId]: [] })),
        );
    }
  }

  if (loading) {
    return <div className="adm__body">Loading…</div>;
  }
  if (error || !customer) {
    return (
      <div className="adm__body">{error || "Customer not found"}</div>
    );
  }

  const name = fullName(customer);

  return (
    <div className="adm__body">
      <div className="adm__page-head">
        <div className="adm__title-stack">
          <div className="meta">↗ CUSTOMER</div>
          <h1 className="h1" style={{ fontSize: 36, margin: 0 }}>
            {name || customer.email || "Customer"}
          </h1>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/customers" })}>
          ← Back
        </Button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(280px, 1fr) 2fr",
          gap: 20,
          alignItems: "start",
        }}
      >
        <Card>
          <CardHeader>
            <h3>Profile</h3>
            <span className="meta">
              {customer.emailVerified ? "VERIFIED" : "UNVERIFIED"}
            </span>
          </CardHeader>
          <div style={infoRow}>
            <span style={infoKey}>Name</span>
            <span style={infoVal}>{name || "—"}</span>
          </div>
          <div style={infoRow}>
            <span style={infoKey}>Email</span>
            <span style={infoVal}>{customer.email ?? "—"}</span>
          </div>
          <div style={infoRow}>
            <span style={infoKey}>Phone</span>
            <span style={infoVal}>{customer.phone ?? "—"}</span>
          </div>
          <div style={infoRow}>
            <span style={infoKey}>Joined</span>
            <span style={infoVal}>{fmtDate(customer.createdAt)}</span>
          </div>
        </Card>

        <Card>
          <CardHeader>
            <h3>Watches</h3>
            <span className="meta">{watches.length} TOTAL</span>
          </CardHeader>
          <CardContent style={{ padding: 0 }}>
            {watches.length === 0 ? (
              <div style={{ padding: 22, color: "var(--ink-mute)", fontSize: 13 }}>
                No watches on this account.
              </div>
            ) : (
              watches.map((w) => (
                <div key={w.id} style={{ borderBottom: "1px solid var(--line)" }}>
                  <div
                    onClick={() => toggleWatch(w.id)}
                    style={{
                      padding: "16px 22px",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      cursor: "pointer",
                    }}
                  >
                    {w.photoDataUrl ? (
                      <img
                        src={w.photoDataUrl}
                        alt={w.name}
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "1px solid var(--line)",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 6,
                          border: "1px solid var(--line)",
                        }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14 }}>{w.name}</div>
                      <div
                        style={{
                          fontFamily: "var(--f-mono)",
                          fontSize: 11,
                          color: "var(--ink-mute)",
                          letterSpacing: "0.06em",
                        }}
                      >
                        {[w.brand, w.model].filter(Boolean).join(" · ") || "—"}
                        {w.targetBph ? ` · ${w.targetBph} bph` : ""}
                      </div>
                    </div>
                    <span className="meta">
                      {expanded === w.id ? "HIDE" : "RUNS"}
                    </span>
                  </div>

                  {expanded === w.id && (
                    <div style={{ padding: "0 22px 16px" }}>
                      {!measurements[w.id] ? (
                        <div style={{ padding: 12, color: "var(--ink-mute)", fontSize: 12 }}>
                          Loading runs…
                        </div>
                      ) : measurements[w.id].length === 0 ? (
                        <div style={{ padding: 12, color: "var(--ink-mute)", fontSize: 12 }}>
                          No recordings.
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date</TableHead>
                              <TableHead>Rate (s/d)</TableHead>
                              <TableHead>Beat error (ms)</TableHead>
                              <TableHead>Amplitude (°)</TableHead>
                              <TableHead>BPH</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {measurements[w.id].map((m) => (
                              <TableRow key={m.id}>
                                <TableCell className="mono">
                                  {fmtDate(m.timestamp)}
                                </TableCell>
                                <TableCell className="mono">
                                  {bestRate(m) > 0 ? "+" : ""}
                                  {bestRate(m).toFixed(1)}
                                </TableCell>
                                <TableCell className="mono">
                                  {m.beatError.toFixed(1)}
                                </TableCell>
                                <TableCell className="mono">
                                  {m.amplitude != null ? Math.round(m.amplitude) : "—"}
                                </TableCell>
                                <TableCell className="mono">{m.bph}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
